/**
 * Fetch wrapper — pakai cookie `mts_session` (di-set backend on login).
 * `credentials: 'include'` supaya cookie ke-attach di request lintas origin
 * (dev: vite proxy, prod: same origin).
 */
export interface ApiError extends Error {
  status: number;
  body?: unknown;
}

async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  // Content-Type application/json HANYA kalau ada body — Fastify reject
  // POST tanpa body kalau content-type di-set ke JSON.
  const headers: Record<string, string> = { ...(init.headers as Record<string, string> ?? {}) };
  if (init.body !== undefined && init.body !== null && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers,
  });
  const text = await res.text();
  const body = text ? tryJson(text) : null;
  if (!res.ok) {
    // Nest validation errors: `{"message":["field must be..."], "error":"Bad Request"}`
    // Domain errors: `{"error":"slug_taken", "message":"...", "statusCode":409}`
    // Fallback: raw status.
    let errorMsg = `http_${res.status}`;
    if (typeof body === "object" && body) {
      const b = body as { message?: unknown; error?: unknown };
      if (Array.isArray(b.message) && b.message.length > 0) {
        errorMsg = b.message.map((m) => String(m)).join(" · ");
      } else if (typeof b.message === "string" && b.message) {
        errorMsg = b.message;
      } else if (typeof b.error === "string" && b.error) {
        errorMsg = b.error;
      }
    }
    const err = new Error(errorMsg) as ApiError;
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return (body ?? (null as unknown)) as T;
}

function tryJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    }),
  patch: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    }),
  del: <T>(path: string) => apiFetch<T>(path, { method: "DELETE" }),
};
